import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PRODUCT_CODE } from '../../../enums/productCode.enum';
import { handleError } from '../../../helper/bugsnag';
import { createPaymentRedirect } from '../../../helper/createPaymentRedirect';
import { handlePaymentRedirect } from '../../../helper/handlePaymentRedirect';
import { setPendingPayment } from '../../../helper/paymentPendingStorage';
import UserService from '../../../services/User.service';
import { useAppSelector } from '../../../store/hooks';
import { selectUserInformation } from '../../../store/reducers';
import { selectCurrentProduct } from '../../../store/reducers/productReducers';
import { ErrorPage } from '../../pages/Error/ErrorPage';
import { Header } from '../Header/Header';
import { ProgressStep, ProgressSteps } from '../ProgressSteps/ProgressSteps';
import { Checkout } from './Checkout';

type DocumentCheckoutProps = {
  productCode: PRODUCT_CODE;
};

export function DocumentCheckout({ productCode }: DocumentCheckoutProps) {
  const [mainDoctorId, setMainDoctorId] = useState<number | undefined>(undefined);
  const [doctorRelationsLoaded, setDoctorRelationsLoaded] = useState(false);
  const userInformation = useAppSelector(selectUserInformation);
  const fetchedProduct = useAppSelector(selectCurrentProduct);
  const navigate = useNavigate();

  const label = fetchedProduct?.label || 'Zahlung';

  const normalizeDoctorType = (doctorType?: string | number): 'main' | 'sub' | undefined => {
    if (doctorType === 1 || doctorType === '1' || `${doctorType}`.toLowerCase().includes('main')) {
      return 'main';
    }
    if (doctorType === 2 || doctorType === '2' || `${doctorType}`.toLowerCase().includes('sub')) {
      return 'sub';
    }

    return undefined;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDoctorRelations = async () => {
      try {
        const response = await UserService.getDoctors();

        if (response.error || !Array.isArray(response.data)) {
          return;
        }

        const mainRelation = response.data.find(
          (relation: any) => normalizeDoctorType(relation?.doctorType) === 'main'
        );

        if (isMounted && mainRelation?.doctorId) {
          setMainDoctorId(mainRelation.doctorId);
        }
      } catch (error) {
        console.error('Error fetching doctor relations for checkout', error);
      } finally {
        if (isMounted) {
          setDoctorRelationsLoaded(true);
        }
      }
    };

    fetchDoctorRelations();

    return () => {
      isMounted = false;
    };
  }, []);

  const lastAppointment = userInformation.appointment
    .filter(({ carriedOut }) => carriedOut === 1) // tookPlace
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];

  const doctorId = mainDoctorId ?? lastAppointment?.doctorId;

  if (!doctorId && doctorRelationsLoaded && !lastAppointment) {
    return <ErrorPage message='Es wurde kein Termin gefunden.' />;
  }

  if (fetchedProduct?.isDoctorPayment === true && doctorRelationsLoaded && !doctorId) {
    return <ErrorPage message='Bitte wähle einen Arzt aus, um fortzufahren.' />;
  }

  const handleSubmit = async (couponCode?: string): Promise<any> => {
    try {
      const documentRequestSuccess = new URL('/certificate-request-status', window.location.origin).href;

      const redirectUrl = fetchedProduct?.price === 0 ? documentRequestSuccess : createPaymentRedirect();
      const response = await UserService.createPaymentForProduct({
        productCode,
        couponCode,
        doctorId:
          fetchedProduct?.isDoctorPayment === true
            ? doctorId // Product that _must_ have a doctor ID.
            : fetchedProduct?.isDoctorPayment === false
              ? undefined // Product that _must not_ have a doctor ID.
              : doctorId, // Product that may or may not have a doctor ID.
        redirectUrl,
      });

      if (response.error) {
        console.error('Error creating checkout', response);

        return { success: false, error: response };
      } else {
        const paymentUrl = response.data?.data?.redirectUrl;

        if (paymentUrl) {
          setPendingPayment({
            startedAt: Date.now(),
            sourcePath: `${window.location.pathname}${window.location.search}`,
            novalnetUrl: paymentUrl,
            contextType: 'product',
            contextId: fetchedProduct?.code,
          });
          handlePaymentRedirect(paymentUrl);
        } else {
          window.location.href = redirectUrl;
        }

        return { success: true };
      }
    } catch (error) {
      handleError(error, { info: 'Exception during checkout creation' });
      console.error('Error creating checkout', error);

      return { success: false, error };
    }
  };

  const details: Array<{ label: string; value: string | React.ReactNode }> = [
    {
      label: 'Produkt',
      value: label,
    },
  ];

  return (
    <>
      <Header showBackButton={true} barTitle={label} />
      <div className='max-w-[550px] mx-auto mt-3 mb-4'>
        <ProgressSteps>
          <ProgressStep title='Bestätigung' active />
          <ProgressStep title='Zahlung' />
        </ProgressSteps>
      </div>
      <Checkout
        productCode={productCode}
        itemDetailsTitle='Produktdetails'
        itemDetails={details}
        handleSubmit={handleSubmit}
        handleBack={() => navigate(-1)}
      />
    </>
  );
}
